/**
 * Realtime WebSocket protocol — versioned contract between SwarmFeedDO
 * and the dashboard. Phase 13 R2 #282.
 *
 * Shape :
 *   - Server → Client : SwarmEvent (discriminated union by `type`)
 *   - Client → Server : SwarmCommand (id + type + payload)
 *   - Server → Client : SwarmAck (response to a command, by command id)
 *   - Both ways       : ping / pong (heartbeat, every 30s)
 *
 * All payloads are parsed with Zod at both ends. Versioning : bump
 * PROTOCOL_VERSION on any breaking change.
 *
 * Adding new events : append a new z.object({...}) variant inside the
 * SwarmEvent z.discriminatedUnion below, in the section matching the
 * agent that emits it. Then add a `publishEvent` call in that agent.
 */

import { z } from "zod";

export const PROTOCOL_VERSION = 1 as const;

// ─── Server → Client : SwarmEvent ────────────────────────────────────

export const SwarmEvent = z.discriminatedUnion("type", [
  // ── system ────────────────────────────────────────────────────────
  z.object({
    type: z.literal("system.connected"),
    at: z.string(),
    payload: z.object({
      version: z.number(),
      agents: z.array(z.string()),
    }),
  }),
  z.object({
    type: z.literal("system.error"),
    at: z.string(),
    payload: z.object({
      message: z.string(),
      code: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal("system.action_failed"),
    at: z.string(),
    payload: z.object({
      agent: z.string(),
      action: z.string(),
      errorMessage: z.string(),
      engagementId: z.string().optional(),
    }),
  }),

  // ── marketing (A1 #285) ───────────────────────────────────────────
  z.object({
    type: z.literal("marketing.heartbeat_tick"),
    at: z.string(),
    payload: z.object({
      cadence: z.enum(["4h", "15min"]),
      processed: z.number(),
      durationMs: z.number().optional(),
    }),
  }),
  z.object({
    type: z.literal("marketing.sequence_advanced"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      sequenceId: z.string(),
      stepNumber: z.number(),
      channel: z.enum(["email", "linkedin_dm", "linkedin_invite"]),
    }),
  }),
  z.object({
    type: z.literal("marketing.post_published"),
    at: z.string(),
    payload: z.object({
      postId: z.string(),
      accountId: z.string(),
      accountName: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal("marketing.comment_made"),
    at: z.string(),
    payload: z.object({
      accountId: z.string(),
      targetUrl: z.string().optional(),
      commentLength: z.number(),
    }),
  }),
  z.object({
    type: z.literal("marketing.warm_score_updated"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      fromScore: z.number(),
      toScore: z.number(),
      reason: z.string(),
    }),
  }),

  // ── support (A2 #286) ─────────────────────────────────────────────
  z.object({
    type: z.literal("support.heartbeat_tick"),
    at: z.string(),
    payload: z.object({
      processed: z.number(),
    }),
  }),
  z.object({
    type: z.literal("support.auto_reply_drafted"),
    at: z.string(),
    payload: z.object({
      draftId: z.string(),
      leadId: z.string(),
      channel: z.enum(["email", "linkedin_dm"]),
      sourceTextPreview: z.string().max(160),
    }),
  }),
  z.object({
    type: z.literal("support.auto_reply_sent"),
    at: z.string(),
    payload: z.object({
      draftId: z.string(),
      leadId: z.string(),
      channel: z.enum(["email", "linkedin_dm"]),
    }),
  }),
  z.object({
    type: z.literal("support.email_correlated"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      messageId: z.string(),
      fromEmail: z.string(),
      subject: z.string().max(240),
    }),
  }),
  z.object({
    type: z.literal("support.suppression_added"),
    at: z.string(),
    payload: z.object({
      email: z.string(),
      reason: z.enum(["unsubscribe", "hard_bounce", "complaint", "manual"]),
    }),
  }),

  // ── growth (A3 #287) ──────────────────────────────────────────────
  z.object({
    type: z.literal("growth.heartbeat_tick"),
    at: z.string(),
    payload: z.object({
      processed: z.number(),
    }),
  }),
  z.object({
    type: z.literal("growth.pharow_enrolled"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      companyName: z.string(),
      bantScore: z.number().nullable(),
      triggerType: z.string(),
    }),
  }),
  z.object({
    type: z.literal("growth.lead_scored"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      bantScore: z.number(),
    }),
  }),
  z.object({
    type: z.literal("growth.sequence_kicked_off"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      sequenceId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("growth.bant_recomputed"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      fromScore: z.number().nullable(),
      toScore: z.number(),
    }),
  }),
  z.object({
    type: z.literal("growth.cta_clicked"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      engagementId: z.string(),
      url: z.string(),
    }),
  }),
  z.object({
    type: z.literal("growth.meeting_booked"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      engagementId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("growth.cold_audit_requested"),
    at: z.string(),
    payload: z.object({
      leadId: z.string(),
      engagementId: z.string(),
    }),
  }),

  // ── ceo / verifier / integrator / engineering / fundraising (A4 #288)
  z.object({
    type: z.literal("ceo.heartbeat_tick"),
    at: z.string(),
    payload: z.object({}).loose(),
  }),
  z.object({
    type: z.literal("ceo.decision_made"),
    at: z.string(),
    payload: z.object({
      decisionId: z.string(),
      escalationFrom: z.string(),
      verdict: z.string(),
      severityLevel: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal("verifier.heartbeat_tick"),
    at: z.string(),
    payload: z.object({}).loose(),
  }),
  z.object({
    type: z.literal("verifier.verdict_emitted"),
    at: z.string(),
    payload: z.object({
      actionId: z.string(),
      verdict: z.enum(["PASS", "CONDITIONAL", "FAIL", "BLOCK"]),
      targetAgent: z.string(),
    }),
  }),
  z.object({
    type: z.literal("integrator.heartbeat_tick"),
    at: z.string(),
    payload: z.object({}).loose(),
  }),
  z.object({
    type: z.literal("integrator.audit_generated"),
    at: z.string(),
    payload: z.object({
      auditKey: z.string(),
      engagementId: z.string(),
      sizeBytes: z.number(),
    }),
  }),
  z.object({
    type: z.literal("integrator.email_sent"),
    at: z.string(),
    payload: z.object({
      to: z.string(),
      subject: z.string().max(240),
      engagementId: z.string(),
      leadId: z.string().nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal("engineering.heartbeat_tick"),
    at: z.string(),
    payload: z.object({}).loose(),
  }),
  z.object({
    type: z.literal("engineering.deploy_done"),
    at: z.string(),
    payload: z.object({
      versionId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("fundraising.heartbeat_tick"),
    at: z.string(),
    payload: z.object({}).loose(),
  }),
]);
export type SwarmEvent = z.infer<typeof SwarmEvent>;

// ─── Client → Server : SwarmCommand ──────────────────────────────────

export const SwarmCommand = z.object({
  id: z.string(),
  type: z.string(),
  payload: z.record(z.string(), z.unknown()).optional(),
});
export type SwarmCommand = z.infer<typeof SwarmCommand>;

// ─── Server → Client : SwarmAck ──────────────────────────────────────

export const SwarmAck = z.discriminatedUnion("status", [
  z.object({
    type: z.literal("ack"),
    id: z.string(),
    status: z.literal("executing"),
    at: z.string(),
  }),
  z.object({
    type: z.literal("ack"),
    id: z.string(),
    status: z.literal("done"),
    at: z.string(),
    result: z.unknown().optional(),
  }),
  z.object({
    type: z.literal("ack"),
    id: z.string(),
    status: z.literal("error"),
    at: z.string(),
    error: z.string(),
  }),
]);
export type SwarmAck = z.infer<typeof SwarmAck>;

// ─── Heartbeat ───────────────────────────────────────────────────────

export const PING = { type: "ping" } as const;
export const PONG = { type: "pong" } as const;

// ─── Agents declared in the system.connected payload ─────────────────

export const KNOWN_AGENTS = [
  "marketing",
  "support",
  "growth",
  "ceo",
  "verifier",
  "integrator",
  "engineering",
  "fundraising",
] as const;
